'use client';

import React from 'react';
import {
  Check,
  ChevronsUpDown,
  AlertCircle,
  Loader2,
  Workflow,
  Edit,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { usePaymentFlowsListWithProcessors } from '@/lib/api/paymentFlows';
import { useStore } from '@/lib/api/stores';
import { getIntegrationImage } from '@/lib/utils/integration-utils';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { Link, useLocation } from 'react-router-dom';

interface PaymentFlowSelectorProps {
  storeId: string;
  value?: string;
  onChange: (flowId: string | undefined) => void;
  disabled?: boolean;
}

export function PaymentFlowSelector({
  storeId,
  value,
  onChange,
  disabled = false,
}: PaymentFlowSelectorProps) {
  const { t } = useTranslation('storefront');
  const [open, setOpen] = React.useState(false);
  const { orgRoutes } = useOrgNavigate();
  const location = useLocation();

  const getEditUrl = (flowId: string) => {
    const currentUrl = `${location.pathname}${location.search}`;
    const editPath = orgRoutes.PAYMENT_FLOW_EDIT(flowId);
    return `${editPath}?returnUrl=${encodeURIComponent(currentUrl)}`;
  };

  const { data: flowsData, isLoading: isLoadingFlows } = usePaymentFlowsListWithProcessors();
  const { data: storeData, isLoading: isLoadingStore } = useStore(storeId);

  const flows = flowsData?.flows || [];
  const defaultFlowId = storeData?.selectedPaymentFlowId ?? undefined;
  const isLoading = isLoadingFlows || isLoadingStore;

  const selectedFlow = value ? flows.find((f) => f.id === value) : null;
  const defaultFlow = defaultFlowId ? flows.find((f) => f.id === defaultFlowId) : null;
  const effectiveFlow = selectedFlow || defaultFlow;

  const getProcessorConfigs = (flow: any) => {
    return flow.processorConfigs?.filter((c: any) => !c.disabled) || [];
  };

  const handleSelect = (flowId: string) => {
    onChange(flowId);
    setOpen(false);
  };

  const handleReset = () => {
    onChange(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('paymentFlow.loadingFlows')}
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-muted-foreground">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        <p className="text-xs">{t('paymentFlow.noFlowsConfigured')}</p>
      </div>
    );
  }

  // Currently using store default — show it with option to override
  if (!value) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            disabled={disabled}
            className="flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <span>{defaultFlow?.name || t('paymentFlow.defaultFlow')}</span>
            <ChevronsUpDown className="h-2.5 w-2.5 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <FlowList
            flows={flows}
            defaultFlowId={defaultFlowId}
            selectedId={value}
            onSelect={handleSelect}
            getProcessorConfigs={getProcessorConfigs}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Using a specific override — show compact card with reset option
  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 dark:border-primary/15 dark:bg-primary/5">
      <div className="flex items-center gap-2 px-3 py-2">
        <Workflow className="h-3.5 w-3.5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-xs font-medium text-slate-800 dark:text-slate-200">
              {selectedFlow?.name || t('paymentFlow.unknownFlow')}
            </span>
            {selectedFlow?.threeDsEnabled && (
              <Badge variant="secondary" className="h-4 border-0 bg-green-100 px-1 text-[9px] text-green-700 dark:bg-green-900/40 dark:text-green-300">
                3DS
              </Badge>
            )}
            {selectedFlow?.id === defaultFlowId && (
              <Badge variant="secondary" className="h-4 border-0 px-1 text-[9px]">
                default
              </Badge>
            )}
          </div>
          {selectedFlow && (
            <div className="mt-0.5 flex items-center gap-1">
              {getProcessorConfigs(selectedFlow).slice(0, 3).map((config: any) => (
                <div key={config.processorId} className="flex items-center gap-1">
                  {config.processor?.type && (
                    <img
                      src={getIntegrationImage(config.processor.type)}
                      alt={config.processor.type}
                      className="h-3 w-3 object-contain"
                    />
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {config.processor?.name || ''}
                  </span>
                </div>
              ))}
              {getProcessorConfigs(selectedFlow).length > 3 && (
                <span className="text-[10px] text-muted-foreground">
                  +{getProcessorConfigs(selectedFlow).length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {selectedFlow && (
            <Link to={getEditUrl(selectedFlow.id)} title="Edit flow">
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <Edit className="h-3 w-3 text-muted-foreground" />
              </Button>
            </Link>
          )}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6" disabled={disabled}>
                <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="end">
              <FlowList
                flows={flows}
                defaultFlowId={defaultFlowId}
                selectedId={value}
                onSelect={handleSelect}
                getProcessorConfigs={getProcessorConfigs}
              />
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={handleReset}
            title={t('paymentFlow.resetToDefault')}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FlowList({
  flows,
  defaultFlowId,
  selectedId,
  onSelect,
  getProcessorConfigs,
}: {
  flows: any[];
  defaultFlowId?: string;
  selectedId?: string;
  onSelect: (id: string) => void;
  getProcessorConfigs: (flow: any) => any[];
}) {
  const { t } = useTranslation('storefront');
  return (
    <Command>
      <CommandList>
        <CommandEmpty>{t('paymentFlow.noFlowsFound')}</CommandEmpty>
        <CommandGroup>
          {flows.map((flow) => {
            const isDefault = flow.id === defaultFlowId;
            const isSelected = selectedId === flow.id;
            const processors = getProcessorConfigs(flow);

            return (
              <CommandItem
                key={flow.id}
                value={`${flow.id}-${flow.name}`}
                onSelect={() => onSelect(flow.id)}
                className="flex items-center gap-2.5 px-3 py-2"
              >
                <Check className={cn('h-3.5 w-3.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{flow.name}</span>
                    {isDefault && (
                      <Badge variant="outline" className="h-4 shrink-0 px-1 text-[9px]">
                        default
                      </Badge>
                    )}
                    {flow.threeDsEnabled && (
                      <Badge className="h-4 shrink-0 border-0 bg-green-100 px-1 text-[9px] text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        3DS
                      </Badge>
                    )}
                  </div>
                  {processors.length > 0 && (
                    <div className="mt-0.5 flex items-center gap-1.5">
                      {processors.slice(0, 3).map((config: any) => (
                        config.processor?.type && (
                          <img
                            key={config.processorId}
                            src={getIntegrationImage(config.processor.type)}
                            alt={config.processor.type}
                            className="h-3 w-3 object-contain opacity-60"
                          />
                        )
                      ))}
                      <span className="text-[10px] text-muted-foreground">
                        {processors.length} processor{processors.length !== 1 ? 's' : ''}
                        {flow.fallbackMode ? ' • fallback' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
